import { z } from "zod";

// TES-841 (Emelie 2026-09-22, TES-840 punkt 3) — the doctor's STORED clinical
// phase choice. It is the branch everything after a positive treatment
// decision hangs off, and it is taken by a human, never derived:
//
//   "Före varje positivt behandlingsbeslut ska Kirill uttryckligen välja:"
//   "ACTIVE_TREATMENT / WEIGHT_STABILISATION. Valet ska finnas både efter"
//   "vårdmöte och i den asynkrona receptövertagsgranskningen, vara"
//   "obligatoriskt, auditloggat och aldrig härledas/defaultas."
//
// ⚠️ NOT `TREATMENT_PHASES` in ./patient-journey-state. Those eight values are
// TIME-derived from the activation instant and never stored — that file says
// so itself ("Derived per beslut — NOT stored"). This one is a stored clinical
// decision. Two different things with nearly the same word; reusing the wrong
// one makes the phase impossible to trust, because a value that can be
// recomputed is a value nobody has to have chosen.
//
// It lives in its own module rather than in ./prescription-transfer-review
// because it is no longer transfer-only: both decision surfaces write it, and
// a phase that is a transfer concept in the type system invites exactly the
// "receptövertag means maintenance" inference Emelie rules out — "Fasvalet är"
// "oberoende av patientgrupp och abonnemang. Receptövertag betyder alltså inte"
// "automatiskt viktstabilisering."
//
// NAMING — settled by Emelie, 2026-09-22 section 5, and it is a THREE-layer
// answer, not one name:
//
//   "Befintligt internt maintenance får användas nu, men etiketten ska vara"
//   "Viktstabilisering, kontraktet mappar till WEIGHT_STABILISATION och"
//   "värdet får inte härledas från abonnemanget."
//
//   stored (DB)  `maintenance`           — unchanged, so no migration and no
//                                          rewrite of the rows QA already has
//   contract     `weight_stabilisation`  — what crosses the wire and what
//                                          every branch in the code compares
//                                          against
//   label (sv)   "Viktstabilisering"     — what Kirill reads in admin
//
// The mapping is therefore deliberate, not a leftover: `maintenance` survives
// in the column and in old audit rows, and the two functions below are the
// only place that word is allowed to mean this. Everything above them speaks
// Emelie's name. If she later asks for the column to be renamed too, that is a
// migration plus deleting these two functions — nothing else moves.
//
// The last clause of her sentence is a rule with teeth and it is tested:
// the phase is NEVER read off the subscription. She keeps them as separate
// dimensions (section 10) and a patient can be in weight stabilisation on any
// subscription, so no code may infer one from the other in either direction.
export const CLINICAL_PHASES = [
  "active_treatment",
  "weight_stabilisation",
] as const;
export const clinicalPhaseSchema = z.enum(CLINICAL_PHASES);
export type ClinicalPhase = z.infer<typeof clinicalPhaseSchema>;

// The column value each contract phase is written as. `active_treatment` is
// already spelled the same on both sides; only the second value maps.
const STORED: Readonly<Record<ClinicalPhase, string>> = {
  active_treatment: "active_treatment",
  weight_stabilisation: "maintenance",
};

// Every spelling the column may legitimately hold, read back to the contract
// name. `weight_stabilisation` is accepted as well as written: a row saved
// before this mapping existed, or after a later migration, must not read as a
// patient with no phase at all.
const FROM_STORED: Readonly<Record<string, ClinicalPhase>> = {
  active_treatment: "active_treatment",
  maintenance: "weight_stabilisation",
  weight_stabilisation: "weight_stabilisation",
};

/**
 * What to write into `Treatment.clinicalPhase`.
 *
 * Both decision surfaces go through here, so the column can only ever acquire
 * a spelling `readStoredClinicalPhase` knows. A write site that skipped it
 * would store the contract name for some patients and the internal one for
 * others, and the branch reading them would silently split its own population
 * in two.
 */
export function storedClinicalPhase(phase: ClinicalPhase): string {
  return STORED[phase];
}

/**
 * The stored phase, or `null` when the doctor has not chosen one.
 *
 * Every read goes through here. The column is a plain `String?` (a DB enum
 * would have made the internal name a migration to change), so a caller
 * comparing it to a literal is comparing against whichever spelling happened
 * to be written that month. And `null` has to stay `null`: "Saknad fas får"
 * "inte defaulta" — a missing phase is a state to refuse on, never one to fill
 * in. An unrecognised value reads as absent rather than as a guess, which
 * routes it into the same refusal.
 */
export function readStoredClinicalPhase(
  value: string | null | undefined,
): ClinicalPhase | null {
  if (!value) return null;
  return FROM_STORED[value] ?? null;
}

// TES-840 section 14 — the functional event names in Drive are a NAMING
// question, not missing plumbing: "Funktionella namn i Drive behöver inte nya"
// "kolumner om befintlig kod uttrycker samma semantik. Dokumentera mappning."
//
// So this is the mapping, written down rather than built. Nothing below is a
// new event; each row names the thing that already fires or is already stored,
// and a reader grepping for the Drive name lands here.
//
//   viktstabilisering aktiverad  ↔  the `phase_changed` DomainEvent
//       Already fired by the TES-280 maintenance sweep
//       (apps/api/src/cycle/maintenance-sweep.job.ts), keyed
//       `phase_changed:maintenance:<patientId>`. Same word, same meaning as
//       the stored value above — which is the other reason renaming the column
//       would not have been free.
//
//   stabilisation_plan_activated  ↔  the same `phase_changed` event
//
//   "första Ella-dos" / first_dose_in_ella_at  ↔  `first_dose_reported`
//       DomainEvent's occurredAt, read through `firstDoseReportedAt`
//       (apps/api/src/patient/first-activation-anchor.ts). It is the day the
//       patient names, not the day she told us — "DT-01 startar på valt"
//       "dosdatum".
//
// No FLOW_EVENTS entries were added for any of them. A second event carrying
// the same fact is how two surfaces start disagreeing about when it happened.
